"use client";

import { useState, useEffect, useMemo } from "react";
import { useTranslations, useLocale } from "next-intl";
import {
  AreaChart,
  Area,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  BarChart2,
  TrendingUp,
  TrendingDown,
  Leaf,
  Wallet,
  Briefcase,
  Diamond,
  LayoutGrid,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { formatCurrency, formatPercent, formatDate } from "@/lib/formatters";
import { useQuery } from "@tanstack/react-query";
import { portfolioApi } from "@/lib/api/portfolio";
import { computeAnnualizedPerformance } from "@/lib/portfolio-kpis";
import { activitiesApi } from "@/lib/api/activities";
import { propertiesApi } from "@/lib/api/properties";
import { PortfolioDistributionSection } from "@/components/overview/PortfolioDistributionSection";
import { GoalDeviationChart } from "@/components/goals/GoalDeviationChart";
import { buildGoalRows } from "@/lib/goals";
import type { GoalEntry } from "@/components/goals/EditGoalsDialog";


const NET_WORTH_DATA = [
  { d: "Jan '23", v: 42000 },
  { d: "Feb '23", v: 43800 },
  { d: "Mar '23", v: 46500 },
  { d: "Apr '23", v: 48200 },
  { d: "May '23", v: 50100 },
  { d: "Jun '23", v: 52800 },
  { d: "Jul '23", v: 55300 },
  { d: "Aug '23", v: 57000 },
  { d: "Sep '23", v: 59500 },
  { d: "Oct '23", v: 62000 },
  { d: "Nov '23", v: 65500 },
  { d: "Dec '23", v: 71000 },
  { d: "Jan '24", v: 75000 },
  { d: "Feb '24", v: 80500 },
  { d: "Mar '24", v: 86000 },
  { d: "Apr '24", v: 92000 },
  { d: "May '24", v: 99000 },
  { d: "Jun '24", v: 107000 },
  { d: "Jul '24", v: 116000 },
  { d: "Aug '24", v: 122000 },
  { d: "Sep '24", v: 128000 },
  { d: "Oct '24", v: 135000 },
  { d: "Nov '24", v: 142000 },
  { d: "Dec '24", v: 148398 },
];

interface TooltipPayload {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

function ChartTooltip({ active, payload, label, baseCurrency, isPercent }: TooltipPayload & { baseCurrency: string; isPercent?: boolean }) {
  const locale = useLocale();
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-2 border border-border rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-muted mb-1">{label ? formatDate(label, locale) : ""}</p>
      <p className="font-semibold text-text">
        {isPercent ? formatPercent(payload[0].value) : formatCurrency(payload[0].value, baseCurrency)}
      </p>
    </div>
  );
}


export default function OverviewPage() {
  const t = useTranslations("Overview");
  const locale = useLocale();
  const [mounted, setMounted] = useState(false);
  const { data: user } = useCurrentUser();
  const baseCurrency = user?.settings?.base_currency || "EUR";

  const { data: netWorthResponse, isLoading: isNetWorthLoading } = useQuery({
    queryKey: ["portfolio", "net-worth"],
    queryFn: portfolioApi.getNetWorth,
  });

  const { data: holdings = [], isLoading: isHoldingsLoading } = useQuery({
    queryKey: ['portfolio', 'holdings'],
    queryFn: () => portfolioApi.getHoldings(),
    staleTime: 60_000,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ["activities"],
    queryFn: () => activitiesApi.listActivities(),
    staleTime: 60_000,
  });

  const includeProperties = user?.settings?.include_properties ?? false;

  const { data: properties = [] } = useQuery({
    queryKey: ["properties"],
    queryFn: propertiesApi.listProperties,
    staleTime: 60_000,
    enabled: includeProperties,
  });

  const isLoading = isNetWorthLoading || isHoldingsLoading;

  const rawGoals: GoalEntry[] = user?.settings?.goals ?? [];
  const hasGoals = rawGoals.length > 0;

  const { data: projectionPoints = [] } = useQuery({
    queryKey: ["portfolio", "goals-projection"],
    queryFn: portfolioApi.getGoalsProjection,
    staleTime: 60_000,
    enabled: hasGoals,
  });

  const goalRows = useMemo(
    () => buildGoalRows(rawGoals, netWorthResponse?.history ?? [], projectionPoints, includeProperties, new Date().getFullYear()),
    [rawGoals, netWorthResponse, projectionPoints, includeProperties]
  );

  useEffect(() => { setMounted(true); }, []);

  const formatXAxisTick = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
    } catch {
      return dateStr;
    }
  };

  const chartData = netWorthResponse?.history.map((pt) => ({
    date: pt.date,
    v: pt.value,
  })) || [];

  const firstInvestedPointIndex = netWorthResponse?.history 
    ? netWorthResponse.history.findIndex(pt => pt.portfolio_investment > 1e-5)
    : -1;

  const portfolioChartData = netWorthResponse?.history && firstInvestedPointIndex !== -1
    ? netWorthResponse.history.slice(firstInvestedPointIndex).map((pt) => ({
        date: pt.date,
        v: pt.portfolio_return_percent,
      }))
    : [];

  const currentNetWorth = netWorthResponse?.current_net_worth ?? 0;

  // Dynamic KPIs calculations
  const currentCash = netWorthResponse?.history && netWorthResponse.history.length > 0
    ? netWorthResponse.history[netWorthResponse.history.length - 1].liquid
    : 0;

  const totalDividends = holdings.reduce((sum, h) => sum + h.dividend, 0);

  const currentReturn = netWorthResponse?.history && netWorthResponse.history.length > 0
    ? netWorthResponse.history[netWorthResponse.history.length - 1].portfolio_return_percent
    : 0;

  const annualizedPerformance = computeAnnualizedPerformance(netWorthResponse?.history ?? []);

  const STAT_CARDS = [
    { key: "netReturn", value: currentReturn, isPercent: true, Icon: BarChart2, color: "text-sky-400" },
    { key: "annualizedPerformance", value: annualizedPerformance, isPercent: true, Icon: TrendingUp, color: "text-accent" },
    { key: "totalDividends", value: totalDividends, isPercent: false, Icon: Leaf, color: "text-green" },
    { key: "availableCash", value: currentCash, isPercent: false, Icon: Wallet, color: "text-amber-400" },
  ] as const;

  const investedInFunds = holdings
    .filter(h => {
      const sub = h.asset_sub_class?.toUpperCase();
      return sub === "MUTUALFUND" || sub === "MUTUAL_FUNDS" || sub === "ETF";
    })
    .reduce((sum, h) => sum + h.value_in_base_currency, 0);

  const investedInStocks = holdings
    .filter(h => h.asset_sub_class?.toUpperCase() === "STOCK")
    .reduce((sum, h) => sum + h.value_in_base_currency, 0);

  const investedInCrypto = holdings
    .filter(h => {
      const sub = h.asset_sub_class?.toUpperCase();
      const cls = h.asset_class?.toUpperCase();
      return sub === "CRYPTOCURRENCY" || cls === "ALTERNATIVE_INVESTMENT";
    })
    .reduce((sum, h) => sum + h.value_in_base_currency, 0);

  const investedInOthers = holdings
    .filter(h => {
      const sub = h.asset_sub_class?.toUpperCase();
      const cls = h.asset_class?.toUpperCase();
      const isFund = sub === "MUTUALFUND" || sub === "MUTUAL_FUNDS" || sub === "ETF";
      const isStock = sub === "STOCK";
      const isCrypto = sub === "CRYPTOCURRENCY" || cls === "ALTERNATIVE_INVESTMENT";
      return !isFund && !isStock && !isCrypto;
    })
    .reduce((sum, h) => sum + h.value_in_base_currency, 0);

  const INVESTMENT_CARDS = [
    { key: "investedInFunds", value: investedInFunds, Icon: Briefcase, color: "text-accent" },
    { key: "investedInStocks", value: investedInStocks, Icon: TrendingUp, color: "text-sky-400" },
    { key: "investedInCrypto", value: investedInCrypto, Icon: Diamond, color: "text-amber-400" },
    { key: "investedInOthers", value: investedInOthers, Icon: LayoutGrid, color: "text-muted" },
  ] as const;

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-bold tracking-[0.18em] uppercase text-text/70">
        {t("summary")}
      </h2>

      {/* Row 1: Net Worth chart + stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Net Worth card */}
        <div className="md:col-span-2 relative flex flex-col bg-chart-net-worth-bg rounded-xl p-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-sky-400/70 font-semibold">
              {t("totalNetWorth")}
            </p>
            <p className="text-[2.6rem] font-bold text-text leading-tight mt-1.5 tabular-nums">
              {isLoading ? "..." : formatCurrency(currentNetWorth, baseCurrency)}
            </p>
          </div>
          {netWorthResponse && netWorthResponse.yoy_change_percent !== null && (
            <span className={cn(
              "absolute top-5 right-5 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full",
              netWorthResponse.yoy_change_percent >= 0
                ? "text-green bg-green/10"
                : "text-red-500 bg-red-500/10"
            )}>
              {netWorthResponse.yoy_change_percent >= 0 ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              {formatPercent(netWorthResponse.yoy_change_percent)}
            </span>
          )}
          <div className="mt-5 h-44">
            {mounted && !isLoading && (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartData}
                  margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="netWorthGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4A90C4" stopOpacity={0.38} />
                      <stop offset="100%" stopColor="#4A90C4" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#4A6A8A", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatXAxisTick}
                  />
                  <Tooltip
                    content={<ChartTooltip baseCurrency={baseCurrency} />}
                    cursor={{ stroke: "#4A90C4", strokeWidth: 1, strokeDasharray: "4 2" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke="#6BAED6"
                    strokeWidth={1.5}
                    fill="url(#netWorthGrad)"
                    dot={false}
                    activeDot={{ r: 3, fill: "#6BAED6", strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Stat cards */}
        <div className="flex flex-col gap-3">
          {STAT_CARDS.map(({ key, value, isPercent, Icon, color }) => (
            <div
              key={key}
              className="flex-1 bg-surface rounded-xl px-5 py-4 flex items-start justify-between"
            >
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted font-semibold leading-none">
                  {t(key)}
                </p>
                <p className="text-[1.6rem] font-bold text-text mt-2 leading-none tabular-nums">
                  {isLoading ? "..." : (isPercent ? formatPercent(value) : formatCurrency(value, baseCurrency))}
                </p>
              </div>
              <Icon className={cn("h-5 w-5 shrink-0 mt-0.5", color)} />
            </div>
          ))}
        </div>
      </div>

      {/* Row 2: Investment breakdown + Portfolio chart */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Investment cards */}
        <div className="flex flex-col gap-3">
          {INVESTMENT_CARDS.map(({ key, value, Icon, color }) => (
            <div
              key={key}
              className="flex-1 bg-surface rounded-xl px-5 py-4 flex items-center justify-between"
            >
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted font-semibold leading-none">
                  {t(key)}
                </p>
                <p className="text-[1.4rem] font-bold text-text mt-2 leading-none tabular-nums">
                  {isLoading ? "..." : formatCurrency(value, baseCurrency)}
                </p>
              </div>
              <Icon className={cn("h-5 w-5 shrink-0", color)} />
            </div>
          ))}
        </div>

        {/* Portfolio chart */}
        <div className="md:col-span-2 relative flex flex-col bg-chart-portfolio-bg rounded-xl p-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-green/50">
              {t("totalPortfolioValue")}
            </p>
            <p className="text-[2.6rem] font-bold text-text leading-tight mt-1.5 tabular-nums">
              {isLoading ? "..." : formatCurrency(netWorthResponse?.current_portfolio_value ?? 0, baseCurrency)}
            </p>
          </div>
          {netWorthResponse && netWorthResponse.portfolio_yoy_change_percent !== null && (
            <span className={cn(
              "absolute top-5 right-5 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full",
              netWorthResponse.portfolio_yoy_change_percent >= 0
                ? "text-green bg-green/10"
                : "text-red-500 bg-red-500/10"
            )}>
              {netWorthResponse.portfolio_yoy_change_percent >= 0 ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              {formatPercent(netWorthResponse.portfolio_yoy_change_percent)}
            </span>
          )}
          <div className="mt-5 h-44">
            {mounted && !isLoading && (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={portfolioChartData}
                  margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22C55E" stopOpacity={0.65} />
                      <stop offset="100%" stopColor="#22C55E" stopOpacity={0.04} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#2D6B42", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatXAxisTick}
                  />
                  <Tooltip
                    content={<ChartTooltip baseCurrency={baseCurrency} isPercent={true} />}
                    cursor={{ stroke: "#4ADE80", strokeWidth: 1, strokeDasharray: "4 2" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke="#4ADE80"
                    strokeWidth={1.5}
                    fill="url(#portfolioGrad)"
                    dot={false}
                    activeDot={{ r: 3, fill: "#4ADE80", strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Section: Mis objetivos */}
      {hasGoals && (
        <>
          <h2 className="text-sm font-bold tracking-[0.18em] uppercase text-text/70">
            {t("myGoals")}
          </h2>
          <GoalDeviationChart data={goalRows} baseCurrency={baseCurrency} />
        </>
      )}

      {/* Section: Distribución de tu cartera */}
      <h2 className="text-sm font-bold tracking-[0.18em] uppercase text-text/70">
        {t("portfolioDistribution")}
      </h2>
      <PortfolioDistributionSection
        baseCurrency={baseCurrency}
        liquid={currentCash}
        holdings={holdings}
        activities={activities}
        properties={properties}
        includeProperties={includeProperties}
        isLoading={isLoading}
        mounted={mounted}
      />
    </div>
  );
}
